import { msg } from "@lingui/core/macro";
import { createRoute } from "@tanstack/solid-router";
import { type Component, type ComponentProps, createMemo, For } from "solid-js";
import { fromActorRef } from "@fleet/state-machines/solid";
import {
  type DiagnosticItem,
  type DiagnosticSection,
  getApiStatusCategory,
  getApiStatusLabelKey,
  getSyncStatusCategory,
  getSyncStatusLabelKey,
} from "@cartero/shared";
import { Info, RefreshCw, Server, UserCircle, Wifi, WifiOff } from "@fleet/icons";

import { POWERSYNC_URL, SERVER_URL } from "@/lib/runtime-config";
import { appRoute } from "./route";
import {
  Badge,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  GlassSurface,
  PageContainer,
  Separator,
} from "@fleet/ui";
import { Button } from "@/components/ui/button";
import { PageSubHeader } from "@/components/ui/page-subheader";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { CopyDiagnosticsButton } from "@/components/copy-diagnostics-button";
import { useTheme } from "@/providers/theme-provider";
import { useSession } from "@/lib/auth";
import { useI18n } from "@/providers/i18n-provider";

function DiagnosticRow(props: {
  label: string;
  value: string;
  status: "success" | "warning" | "danger" | "neutral";
  icon: Component<ComponentProps<"svg"> & { size?: number | string }>;
}) {
  const getStatusColorClass = () => {
    switch (props.status) {
      case "success": {
        return "text-green-500 bg-green-500/10";
      }
      case "warning": {
        return "text-yellow-500 bg-yellow-500/10";
      }
      case "danger": {
        return "text-red-500 bg-red-500/10";
      }
      case "neutral": {
        return "text-muted-foreground bg-muted/10";
      }
      default: {
        return "text-muted-foreground bg-muted/10";
      }
    }
  };

  const getTextColorClass = () => {
    switch (props.status) {
      case "success": {
        return "text-green-500";
      }
      case "warning": {
        return "text-yellow-500";
      }
      case "danger": {
        return "text-red-500";
      }
      case "neutral": {
        return "text-muted-foreground";
      }
      default: {
        return "text-muted-foreground";
      }
    }
  };

  return (
    <div class="flex items-center justify-between gap-4 py-2">
      <div class="flex items-center gap-3">
        <div
          class={`flex h-8 w-8 items-center justify-center rounded-full ${getStatusColorClass()}`}
        >
          <props.icon size={16} />
        </div>
        <span class="text-sm font-medium">{props.label}</span>
      </div>
      <span class={`text-sm font-bold shrink text-right truncate ${getTextColorClass()}`}>
        {props.value}
      </span>
    </div>
  );
}

/** Active-account index within the `/u/<n>/...` route. */
const ACCOUNT_ROUTE = /\/u\/(?<index>\d+)/u;

/**
 * `location`/`navigator` are intentionally typed as optional: they never are
 * missing in the DOM type —and lint marks `?.` as redundant— but they are outside
 * the browser (SSR, a test), and collecting the diagnostic must not
 * crash exactly when it is most needed.
 */
function dom(): { loc?: Location; nav?: Navigator } {
  return { loc: globalThis.location, nav: globalThis.navigator };
}

/**
 * The only thing the UI adds and the report omits: the icon. It lives outside the
 * model so the model remains serializable and shared.
 */
const ICON_BY_KEY: Record<string, typeof Info> = {
  online: Wifi,
  session: UserCircle,
  api: Server,
  sync: RefreshCw,
};

/**
 * `online` is resolved by checking the RAW value because this screen already
 * distinguished Wifi from WifiOff: fixing the icon in the map would lose that
 * visual warning, precisely the one visible at a glance without reading the text.
 */
function iconFor(item: DiagnosticItem): typeof Info {
  if (item.key === "online") {
    return item.raw === true ? Wifi : WifiOff;
  }
  return ICON_BY_KEY[item.key] ?? Info;
}

function DebugPage() {
  const { connectivityRef } = usePowerSyncContext();
  const state = fromActorRef(connectivityRef);
  const { t, tDynamic } = useI18n();

  const context = createMemo(() => state().context);
  const stateValue = createMemo(() => state().value);

  const isRetryInProgress = createMemo(
    () => stateValue() === "checking" || stateValue() === "manualChecking",
  );

  const handleForceRetry = () => {
    connectivityRef.send({ type: "RETRY" });
  };

  const getApiLabel = () =>
    tDynamic(getApiStatusLabelKey((v) => state().matches(v), context().apiStatus));
  const getApiState = () => getApiStatusCategory((v) => state().matches(v), context().apiStatus);
  const getSyncLabel = () =>
    tDynamic(getSyncStatusLabelKey((v) => state().matches(v), context().syncStatus));
  const getSyncState = () => getSyncStatusCategory((v) => state().matches(v), context().syncStatus);

  const canRetry = createMemo(() => state().can({ type: "RETRY" }));

  const theme = useTheme();
  const session = useSession();

  /**
   * Truncated user ID — never whole: the report is pasted into chats. It is
   * accompanied by the active-account index, which in a multi-account app says
   * WHICH account the person reporting the problem is looking at.
   */
  const truncatedId = () => {
    const id = session.userId();
    const accountIndex = ACCOUNT_ROUTE.exec(dom().loc?.pathname ?? "")?.groups?.index;
    const truncated = id ? `${id.slice(0, 8)}…` : "-";
    // Technical value, not UI text: the `/u/<n>` route reads the same in all eleven
    // languages. Suppress it like the adapter instead of leaving the eslint
    // gate red, as it remained in the canon.
    // oxlint-disable-next-line lingui/no-unlocalized-strings -- technical value
    return accountIndex ? `${truncated} · /u/${accountIndex}` : truncated;
  };

  /**
   * SINGLE SOURCE for the screen: the UI renders this and the button copies the same thing.
   * Previously the screen had its rows and the report had its own object — two
   * sources for the same thing, so adding a row here left the report
   * incomplete without anyone noticing.
   *
   * `key` is stable and language-independent (logic uses it), `label` and
   * `value` are already translated, `status` is only inspected by the UI, and `raw`
   * is inspected by the logic so it does not depend on a translation.
   *
   * NAMED sections, not indexed: `secciones()[1]` coupled the screen to the
   * array order, and inserting one was enough to break it silently.
   */
  const connectivitySection = (): DiagnosticSection => ({
    title: t(msg`Connectivity`),
    items: [
      {
        key: "online",
        label: t(msg`Internet Access`),
        value: context().isOnline ? t(msg`Connected`) : t(msg`Disconnected`),
        status: context().isOnline ? "success" : "danger",
        raw: context().isOnline,
      },
      {
        key: "session",
        label: t(msg`User Session`),
        value: context().isAuthenticated ? t(msg`Active`) : t(msg`None`),
        status: context().isAuthenticated ? "success" : "warning",
        raw: context().isAuthenticated,
      },
      {
        key: "api",
        label: t(msg`API Server (Writes)`),
        value: getApiLabel(),
        status: getApiState(),
        raw: context().apiStatus,
      },
      {
        key: "clientVersion",
        label: t(msg`Client Version`),
        value: `${import.meta.env.APP_VERSION} (${import.meta.env.COMMIT_HASH})`,
        status: "neutral",
      },
      ...(context().serverVersion
        ? [
            {
              key: "serverVersion",
              label: t(msg`Server Version`),
              value: String(context().serverVersion),
              status: "neutral" as const,
            },
          ]
        : []),
      {
        key: "sync",
        label: t(msg`PowerSync (Sync)`),
        value: getSyncLabel(),
        status: getSyncState(),
        // `raw` carries the aggregate state; the machine's RAW state (`stateValue`)
        // is omitted as a row because its label ("Status") is not translated in
        // any catalog and adding it would display it in English across all eleven
        // languages. This follows the same criterion applied to the other strings.
        // Pending the i18n pass.
        raw: context().syncStatus,
      },
    ],
  });

  const systemSection = (): DiagnosticSection => ({
    title: t(msg`System Information`),
    items: [
      // oxlint-disable-next-line lingui/no-unlocalized-strings -- technical value
      { key: "adapter", label: t(msg`Adapter`), value: "WASM + OPFS" },
      { key: "serverUrl", label: t(msg`Server URL`), value: SERVER_URL },
      { key: "powersyncUrl", label: t(msg`PowerSync URL`), value: POWERSYNC_URL },
      {
        key: "mode",
        label: t(msg`Mode`),
        value: import.meta.env.DEV ? "development" : "production",
      },
      {
        key: "appearance",
        // The theme, EFFECTIVE mode, and device mode go in ONE row because
        // this catalog has no translated theme label:
        // splitting it in two would require a new string, which would appear in English across
        // all eleven languages without the i18n gate complaining. All three values
        // are present, which is what matters: divergence is what causes
        // "text in the opposite mode" failures.
        label: t(msg`Appearance`),
        // Technical value, not UI text: same criterion (and same exception) as
        // the adapter above. The canon left this literal UNSUPPRESSED and the
        // eslint gate remained red.
        // oxlint-disable-next-line lingui/no-unlocalized-strings -- technical value
        value: `${theme.tokens()?.id ?? "-"} · ${theme.colorMode()} → effective ${theme.isDark() ? "dark" : "light"} / device ${theme.systemIsDark() ? "dark" : "light"}`,
      },
      {
        key: "account",
        // `User` instead of the canon's `Account`: only the former is in
        // the catalog here, and a new string would appear in English in every language.
        label: t(msg`User`),
        // Truncated: correlatable with server logs, but pasteable into a chat.
        value: truncatedId(),
      },
    ],
  });

  /** What is rendered AND what is copied. One single place. */
  const sections = (): DiagnosticSection[] => [connectivitySection(), systemSection()];

  return (
    <div class="flex flex-1 flex-col overflow-hidden">
      <PageSubHeader title={t(msg`Diagnostics`)} />

      <PageContainer maxWidth="lg" class="space-y-6" containerClass="min-h-0">
        {/* Entrance animation is provided once at the route Outlet
            (app-layout-content). This inner wrapper only handles spacing. */}
        <div class="space-y-6">
          <GlassSurface class="p-4">
            <h2 class="mb-4 text-lg font-semibold">{connectivitySection().title}</h2>
            <div class="space-y-1">
              {/* Renders the SAME model copied by button. Adding a row
                  arriba la hace aparecer aquí y en el informe a la vez. */}
              <For each={connectivitySection().items}>
                {(item) => (
                  <DiagnosticRow
                    label={item.label}
                    value={item.value}
                    status={item.status ?? "neutral"}
                    icon={iconFor(item)}
                  />
                )}
              </For>
            </div>

            <Button
              variant="default"
              class="mt-6 w-full gap-2"
              onClick={handleForceRetry}
              disabled={isRetryInProgress() || !canRetry()}
            >
              <RefreshCw size={18} class={isRetryInProgress() ? "animate-spin" : ""} />
              {t(msg`Force connection retry`)}
            </Button>

            {/* Copy full diagnostics. Manually describing what is on this screen
                pierde la mitad de los datos, y el caso que originó esto —la app
                "sincronizando" para siempre porque el servicio estaba caído— se
                resuelve en segundos con el informe delante. */}
            <div class="mt-3">
              <CopyDiagnosticsButton appName="cartero" sections={sections} />
            </div>
          </GlassSurface>

          <Card>
            <CardHeader>
              <CardTitle>{systemSection().title}</CardTitle>
            </CardHeader>
            <CardContent>
              <div class="space-y-3">
                <For each={systemSection().items}>
                  {(item) => (
                    <>
                      <div class="flex items-center justify-between text-sm">
                        <span class="text-muted-foreground">{item.label}</span>
                        <Badge variant="outline" class="font-mono text-xs">
                          {item.value}
                        </Badge>
                      </div>
                      <Separator />
                    </>
                  )}
                </For>
              </div>
            </CardContent>
          </Card>
        </div>
      </PageContainer>
    </div>
  );
}

export const debugRoute = createRoute({
  getParentRoute: () => appRoute,
  path: "/debug",
  component: DebugPage,
});
